import { CloseOutlined } from '@ant-design/icons';
import {
    Button,
    Checkbox,
    Collapse,
    Input,
    List,
    Modal,
    Radio,
    Spin,
    Tabs,
    Row,
    Col,
    AutoComplete,
    Divider
} from 'antd';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import TestProjectInputSelectionStore from '../../stores/TestProjectInputSelectionStore';
import type { InputBindingAppInput, InputBindingApplication, InputBindingGroup } from '../../types';

type Props = {
    store: TestProjectInputSelectionStore;
    warnOnRemoval?: boolean;
    disabled?: boolean;
    onConfirmTopicRemoval?: (topicId: string) => void;
};

const TopicSelectionStep: React.FC<Props> = ({
    store,
    disabled = false,
    warnOnRemoval = false,
    onConfirmTopicRemoval
}) => {
    const { selectedTopicsAutocomplete, selectedTopicsFilter } = store;

    const getTagsContent = () => {
        return (
            <Collapse>
                {store.currentProjectTagGroups.map(tg => {
                    const filteredTags = store.currentProjectTags.filter(
                        t => t.groupId === tg.id && t.name.toLowerCase().includes(store.topicsSearchTerm.toLowerCase())
                    );

                    if (filteredTags.length === 0) {
                        return null; // Don't render empty tag groups (no tags match the search term)
                    }

                    return (
                        <Collapse.Panel header={tg.name} key={tg.id!}>
                            {filteredTags.map(t => (
                                <div key={t.id}>
                                    <Checkbox
                                        disabled={disabled}
                                        onChange={e => {
                                            if (!e.target.checked) {
                                                confirmTopicRemoval(t.id!);
                                            } else {
                                                store.toggleTopicTag(t.id!, true);
                                            }
                                        }}
                                        checked={store.selectedTopicTags.includes(t.id!)}
                                    >
                                        {t.name}
                                    </Checkbox>
                                </div>
                            ))}
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        );
    };

    const confirmTopicRemoval = (topicId: string) => {
        if (warnOnRemoval && store.isSavedInput(topicId)) {
            if (onConfirmTopicRemoval) {
                onConfirmTopicRemoval(topicId);
                return;
            }

            Modal.confirm({
                title: 'Removing this topic will clear all of its configured baselines when you save.',
                okText: 'Yes',
                cancelText: 'No',
                onOk: () => store.removeTopic(topicId),
                centered: true
            });
        } else {
            store.removeTopic(topicId);
        }
    };

    const getFilteredInputs = (inputs: InputBindingAppInput[]) => {
        return inputs.filter(input => input.name.toLowerCase().includes(store.topicsSearchTerm.toLowerCase()));
    };

    const renderBindingGroup = (application: InputBindingApplication, group: InputBindingGroup) => {
        const sections = _.groupBy(getFilteredInputs(group.inputs), 'section');
        const isSelectedGroup =
            store.selectedAppForBindings === application.id && store.selectedInputGroupForBindings === group.id;

        return (
            <Collapse>
                {Object.entries(sections).map(([section, inputs]) => (
                    <Collapse.Panel header={section} key={section}>
                        {inputs.map(input => (
                            <div key={input.inputId}>
                                <Checkbox
                                    onChange={e => {
                                        if (!e.target.checked) {
                                            confirmTopicRemoval(input.inputId);
                                        } else {
                                            store.toggleInputBinding(input.inputId, true, application.id, group.id);
                                        }
                                    }}
                                    checked={store.selectedInputBindings.includes(input.inputId) && isSelectedGroup}
                                    disabled={disabled || !store.canSelectBindingGroup(application.id, group.id)}
                                >
                                    {input.name}
                                </Checkbox>
                            </div>
                        ))}
                    </Collapse.Panel>
                ))}
            </Collapse>
        );
    };

    const getBindingsContent = () => {
        if (store.isLoadingInputBindingsMetadata) {
            return <Spin />;
        }

        return (
            <Collapse>
                {store.bindingApplications.map(app => {
                    const hasMatchingInputs = app.groups.some(group => getFilteredInputs(group.inputs).length > 0);

                    if (!hasMatchingInputs) {
                        return null;
                    }

                    return (
                        <Collapse.Panel header={app.name} key={app.id}>
                            {app.groups[0]?.id ? (
                                <Collapse>
                                    {app.groups.map(group => (
                                        <Collapse.Panel header={group.name} key={group.id}>
                                            {renderBindingGroup(app, group)}
                                        </Collapse.Panel>
                                    ))}
                                </Collapse>
                            ) : (
                                app.groups.map(group => (
                                    <React.Fragment key={group.id}>{renderBindingGroup(app, group)}</React.Fragment>
                                ))
                            )}
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        );
    };

    const getFieldBindingContent = () => {
        if (store.isLoadingFieldBindingGroups) {
            return <Spin />;
        }

        return (
            <Collapse>
                {store.fieldBindingGroups.map(group => {
                    const filteredInputs = group.fields.filter(input =>
                        input.name.toLowerCase().includes(store.topicsSearchTerm.toLowerCase())
                    );

                    if (filteredInputs.length === 0) {
                        return null; // Don't render empty apps (no inputs match the search term)
                    }

                    return (
                        <Collapse.Panel header={group.name} key={group.id!}>
                            <Collapse>
                                {group.sections.map(section => {
                                    const filteredBySections = _.uniqBy(
                                        filteredInputs.filter(input => input.sectionId === section.sectionId),
                                        'inputId'
                                    );
                                    if (filteredBySections.length === 0) {
                                        return null; // Don't render empty sections (no inputs match the search term)
                                    }

                                    return (
                                        <Collapse.Panel header={section.name} key={section.sectionId}>
                                            {filteredBySections.map(input => (
                                                <div key={input.inputId}>
                                                    <Checkbox
                                                        disabled={disabled}
                                                        onChange={e => {
                                                            if (!e.target.checked) {
                                                                confirmTopicRemoval(input.inputId);
                                                            } else {
                                                                store.toggleTopicProjectField(input.inputId, true);
                                                            }
                                                        }}
                                                        checked={store.selectedProjectFields.includes(input.inputId)}
                                                    >
                                                        {input.name}
                                                    </Checkbox>
                                                </div>
                                            ))}
                                        </Collapse.Panel>
                                    );
                                })}
                            </Collapse>
                        </Collapse.Panel>
                    );
                })}
            </Collapse>
        );
    };

    const getContent = () => {
        switch (store.topicInputsType) {
            case 'Tags':
                return getTagsContent();
            case 'Bindings':
                return getBindingsContent();
            case 'ProjectFields':
                return getFieldBindingContent();
            default:
                return null;
        }
    };

    const getTopicTitle = (item: string) => {
        const name = store.getTopicName(item);

        if (name) {
            return name;
        }

        return <span style={{ color: 'red' }}>{item}</span>;
    };

    return (
        <Tabs activeKey={store.activeTopicsTab} onChange={store.setActiveTopicsTab}>
            <Tabs.TabPane tab="Search topics" key="1">
                <div>
                    <Radio
                        disabled={disabled}
                        name="topicInputType"
                        value="Bindings"
                        checked={store.topicInputsType === 'Bindings'}
                        onChange={e => store.setTopicInputsType(e.target.value)}
                    >
                        IOTA App Bindings
                    </Radio>
                    <Radio
                        disabled={disabled}
                        name="topicInputType"
                        value="Tags"
                        checked={store.topicInputsType === 'Tags'}
                        onChange={e => store.setTopicInputsType(e.target.value)}
                    >
                        Tags
                    </Radio>
                    <Radio
                        disabled={disabled}
                        name="topicInputType"
                        value="ProjectFields"
                        checked={store.topicInputsType === 'ProjectFields'}
                        onChange={e => store.setTopicInputsType(e.target.value)}
                    >
                        Project Fields
                    </Radio>
                </div>
                <br />
                <div>
                    <Input.Search
                        value={store.topicsSearchTerm}
                        disabled={disabled}
                        placeholder="Search topics..."
                        onChange={e => store.setTopicsSearchTerm(e.target.value)}
                    />
                </div>
                <br />
                <div style={{ maxHeight: 'calc(100vh - 380px)', overflow: 'auto' }}>{getContent()}</div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Selected topics" key="2">
                <Row gutter={16} style={{ marginTop: 5 }}>
                    <Col span={12}>
                        <AutoComplete
                            disabled={disabled}
                            style={{ width: '100%' }}
                            options={store.topics
                                .map(t => store.getTopicName(t) ?? '')
                                .filter(t => t.toLowerCase().includes(selectedTopicsAutocomplete.toLowerCase()))
                                .map(topicName => ({
                                    value: topicName,
                                    label: <span style={{ whiteSpace: 'pre' }}>{topicName}</span>
                                }))}
                            value={selectedTopicsAutocomplete}
                            onSearch={store.setSelectedTopicsAutocomplete}
                            placeholder="Search topics..."
                            onSelect={store.filterSelectedTopics}
                            onClear={() => store.filterSelectedTopics('')}
                            allowClear
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    store.filterSelectedTopics(selectedTopicsAutocomplete);
                                }
                            }}
                        />
                    </Col>
                </Row>
                <Divider style={{ marginBottom: 0 }} />
                <List
                    style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}
                    dataSource={
                        selectedTopicsFilter
                            ? store.topics.filter(t =>
                                  store.getTopicName(t)?.toLowerCase().includes(selectedTopicsFilter.toLowerCase())
                              )
                            : store.topics
                    }
                    renderItem={item => (
                        <List.Item
                            actions={[
                                <Button
                                    disabled={disabled}
                                    key={item + '-delete'}
                                    type="link"
                                    icon={<CloseOutlined />}
                                    onClick={() =>
                                        warnOnRemoval ? confirmTopicRemoval(item) : store.removeTopic(item)
                                    }
                                />
                            ]}
                        >
                            <List.Item.Meta title={getTopicTitle(item)} />
                        </List.Item>
                    )}
                />
            </Tabs.TabPane>
        </Tabs>
    );
};

export default observer(TopicSelectionStep);
